import * as AccordionPrimitive from "@radix-ui/react-accordion";
import { ChevronDown, X } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import { AccordionContent, AccordionItem } from "@/components/ui/accordion";
import { cn } from "@/lib/utils";
import {
  countSelectedRows,
  getTagDisplayLabel,
  getTagSelectionState,
  type FilterSelectionState,
} from "./documentFilterRowUtils";
import type { Tag, TagGroup } from "./types";

interface FilterGroupAccordionItemProps {
  group: TagGroup;
  selectedTags: string[];
  onToggle: (filterId: string) => void;
  onClear: () => void;
  clearLabel: string;
}

interface FilterTagRowProps {
  group: TagGroup;
  tag: Tag;
  selectionState: FilterSelectionState;
  showGroupLabel?: boolean;
  onToggle: (filterId: string) => void;
}

export function FilterTagRow({
  group,
  tag,
  selectionState,
  showGroupLabel = false,
  onToggle,
}: FilterTagRowProps) {
  const displayLabel = getTagDisplayLabel(group, tag);
  const checkboxLabel = `${group.label}: ${displayLabel}`;
  const toggle = () => onToggle(tag.id);

  return (
    <div
      className={cn(
        "flex min-h-8 items-center gap-2 px-3 text-xs hover:bg-muted/60",
        !showGroupLabel && tag.isSubcategory && "pl-6"
      )}
    >
      <Checkbox
        checked={selectionState === "checked"}
        indeterminate={selectionState === "indeterminate"}
        onCheckedChange={toggle}
        aria-label={checkboxLabel}
        className="border-muted-foreground/50 data-[state=checked]:border-[#ef4129] data-[state=indeterminate]:border-[#ef4129] data-[state=checked]:bg-[#ef4129] data-[state=indeterminate]:bg-[#ef4129] data-[state=checked]:text-white data-[state=indeterminate]:text-white"
      />
      <button
        type="button"
        onClick={toggle}
        className="flex min-w-0 flex-1 items-center gap-2 py-1.5 text-left focus-visible:outline-none"
      >
        <span className="min-w-0 flex-1 truncate">
          <span className="block truncate">{displayLabel}</span>
          {showGroupLabel && (
            <span className="block truncate text-[10px] leading-3 text-muted-foreground">
              {group.label}
            </span>
          )}
        </span>
        <span className="shrink-0 text-xs text-muted-foreground">({tag.count})</span>
      </button>
    </div>
  );
}

interface FilterRow {
  tag: Tag;
  selectionState: FilterSelectionState;
}

/**
 * Split the group's rows into blocks of a parent followed by its
 * subcategories, in the hierarchical order `useTagGroups` built. Hoisting
 * moves a whole block, so a `pl-6` child can never end up indented under an
 * unrelated tag: a partly-selected parent takes its subtree up with it.
 */
function groupRowsByParent(rows: FilterRow[]): FilterRow[][] {
  return rows.reduce<FilterRow[][]>((blocks, row) => {
    const currentBlock = blocks[blocks.length - 1];
    if (row.tag.isSubcategory && currentBlock) currentBlock.push(row);
    else blocks.push([row]);
    return blocks;
  }, []);
}

function FilterTagList({
  group,
  selectedTags,
  onToggle,
}: Pick<FilterGroupAccordionItemProps, "group" | "selectedTags" | "onToggle">) {
  const blocks = groupRowsByParent(
    group.tags.map((tag) => ({
      tag,
      selectionState: getTagSelectionState(tag, selectedTags),
    }))
  );
  const hasSelection = (block: FilterRow[]) =>
    block.some((row) => row.selectionState !== "unchecked");
  const selectedRows = blocks.filter(hasSelection).flat();
  const unselectedRows = blocks.filter((block) => !hasSelection(block)).flat();

  return (
    <>
      {selectedRows.map((row) => (
        <FilterTagRow key={row.tag.id} group={group} onToggle={onToggle} {...row} />
      ))}
      {selectedRows.length > 0 && unselectedRows.length > 0 && (
        <div role="separator" className="mx-3 my-1 border-t border-border/70" />
      )}
      {unselectedRows.map((row) => (
        <FilterTagRow key={row.tag.id} group={group} onToggle={onToggle} {...row} />
      ))}
    </>
  );
}

export function FilterGroupAccordionItem({
  group,
  selectedTags,
  onToggle,
  onClear,
  clearLabel,
}: FilterGroupAccordionItemProps) {
  const Icon = group.icon;
  const selectedCount = countSelectedRows(selectedTags, [group]);

  return (
    <AccordionItem value={group.id}>
      <AccordionPrimitive.Header className="relative flex">
        <AccordionPrimitive.Trigger className="flex flex-1 items-center gap-2 px-3 py-2.5 pr-2 text-xs font-medium hover:bg-muted/50 focus-visible:bg-muted/50 focus-visible:outline-none [&[data-state=open]_.filter-chevron]:rotate-180">
          <Icon className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
          <span className="min-w-0 truncate">{group.label}</span>
          {selectedCount > 0 && (
            <span className="inline-flex h-4 min-w-4 shrink-0 items-center justify-center rounded-full bg-[#ef4129] px-1 text-[10px] font-semibold text-white">
              {selectedCount}
            </span>
          )}
          <span className="ml-auto h-6 w-6 shrink-0" aria-hidden="true" />
          <ChevronDown className="filter-chevron h-4 w-4 shrink-0 transition-transform duration-200" />
        </AccordionPrimitive.Trigger>
        {selectedCount > 0 && (
          <button
            type="button"
            onClick={onClear}
            aria-label={`${clearLabel}: ${group.label}`}
            className="absolute right-8 top-1/2 z-10 flex h-6 w-6 -translate-y-1/2 items-center justify-center rounded text-muted-foreground hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        )}
      </AccordionPrimitive.Header>
      <AccordionContent className="max-h-[200px] overflow-y-auto overscroll-contain pb-1">
        <FilterTagList group={group} selectedTags={selectedTags} onToggle={onToggle} />
      </AccordionContent>
    </AccordionItem>
  );
}
